/**
 * WordPress dependencies
 */
import { getContext, getElement, store } from '@wordpress/interactivity';

type MenuOpenedBy = {
	click: boolean;
	focus: boolean;
};

type MegaMenuContext = {
	menuOpenedBy: MenuOpenedBy;
	previousFocus?: HTMLElement | null;
	megaMenu?: HTMLElement | null;
	isClickingInside?: boolean;
};

const getMegaMenuContext = getContext< MegaMenuContext >;

function resolveMenuContainer(
	context: MegaMenuContext,
	ref: HTMLElement | null
): HTMLDivElement | null {
	if ( ref && ! context.megaMenu ) {
		context.megaMenu = ( ref.closest( 'li' ) as HTMLElement ) ?? ref;
	}
	return ( context.megaMenu?.querySelector( '.wp-block-phrase-com-mega-menu__menu-container' ) ??
		null ) as HTMLDivElement | null;
}

function animateMenuContainer( menuContainer: HTMLElement ) {
	menuContainer.style.setProperty( '--max-height', `${ menuContainer.scrollHeight }px` );
	menuContainer.addEventListener(
		'transitionend',
		() => menuContainer.style.removeProperty( '--max-height' ),
		{ once: true }
	);
}

// Only load images on first menu display.
function loadImages( menuContainer: HTMLElement ) {
	const imagesToLoad = menuContainer.querySelectorAll< HTMLImageElement | HTMLSourceElement >(
		'img[data-src], img[data-srcset], source[data-srcset]'
	);
	imagesToLoad.forEach( ( imageToLoad ) => {
		if ( imageToLoad.dataset.src ) {
			imageToLoad.src = imageToLoad.dataset.src;
		}
		if ( imageToLoad.dataset.srcset ) {
			imageToLoad.srcset = imageToLoad.dataset.srcset;
		}
	} );
}

const { state, actions } = store( 'phrase-com-blocks/mega-menu', {
	state: {
		get isMenuOpen(): boolean {
			// The menu is opened if either `click` or `focus` is true.
			const context = getMegaMenuContext();
			return Object.values( context.menuOpenedBy ).filter( Boolean ).length > 0;
		},
	},
	actions: {
		toggleMenuOnClick() {
			const { ref } = getElement() as { ref: HTMLElement | null };

			if ( ! ref ) {
				return;
			}

			const context = getMegaMenuContext();

			// Safari won't send focus to the clicked element, so we need to manually place it: https://bugs.webkit.org/show_bug.cgi?id=22261
			if ( window.document.activeElement !== ref ) {
				ref.focus();
			}

			if ( state.isMenuOpen ) {
				actions.closeMenu( context, 'click' );
				actions.closeMenu( context, 'focus' );
			} else {
				const menuWithImages = ref.closest< HTMLElement >(
					'.wp-block-phrase-com-mega-menu[data-has-images]'
				);
				if ( menuWithImages ) {
					menuWithImages.removeAttribute( 'data-has-images' );
					loadImages( menuWithImages );
				}

				context.previousFocus = ref;
				actions.openMenu( context, 'click' );
			}
		},
		closeMenuOnClick() {
			const context = getMegaMenuContext();
			actions.closeMenu( context, 'click' );
			actions.closeMenu( context, 'focus' );
		},
		handleMenuKeydown( event: KeyboardEvent ) {
			if ( state.isMenuOpen && event?.key === 'Escape' ) {
				const context = getMegaMenuContext();
				actions.closeMenu( context, 'click' );
				actions.closeMenu( context, 'focus' );
			}
		},
		handleMenuFocusout( event: FocusEvent ) {
			const context = getMegaMenuContext();

			// Safari fires focusout with relatedTarget === null on mousedown inside
			// the component before the click event fires. Skip closing in that case.
			if ( context.isClickingInside ) {
				context.isClickingInside = false;
				return;
			}

			const menuContainer = context.megaMenu?.querySelector(
				'.wp-block-phrase-com-mega-menu__menu-container'
			);
			const relatedTargetNode =
				event.relatedTarget instanceof Node ? event.relatedTarget : null;
			// If focus is outside menu, and in the document, close menu.
			// event.target === The element losing focus
			// event.relatedTarget === The element receiving focus (if any)
			// When focusout is outside the document,
			// `window.document.activeElement` doesn't change.

			// event.relatedTarget is null when focus moves outside the page (e.g. to the browser UI or devtools).
			if (
				event.relatedTarget === null ||
				( ! menuContainer?.contains( relatedTargetNode ) &&
					event.target !== window.document.activeElement )
			) {
				actions.closeMenu( context, 'click' );
				actions.closeMenu( context, 'focus' );
			}
		},
		handleMenuMousedown( event: MouseEvent ) {
			const context = getMegaMenuContext();
			const { ref } = getElement() as { ref: HTMLElement | null };
			if ( ref?.contains( event.target as Node ) ) {
				context.isClickingInside = true;
			}
		},
		handleMenuMouseup() {
			const context = getMegaMenuContext();
			context.isClickingInside = false;
		},
		openMenu( context: MegaMenuContext, menuOpenedOn: keyof MenuOpenedBy = 'click' ) {
			const { ref } = getElement() as { ref: HTMLElement | null };
			const menuContainer = resolveMenuContainer( context, ref );
			if ( menuContainer ) {
				animateMenuContainer( menuContainer );
			}
			context.menuOpenedBy[ menuOpenedOn ] = true;
		},
		closeMenu( context: MegaMenuContext, menuClosedOn: keyof MenuOpenedBy = 'click' ) {
			context.menuOpenedBy[ menuClosedOn ] = false;

			// Reset the menu reference and button focus when closed.
			if ( ! state.isMenuOpen ) {
				const { ref } = getElement() as { ref: HTMLElement | null };
				const menuContainer = resolveMenuContainer( context, ref );
				if ( menuContainer ) {
					animateMenuContainer( menuContainer );
				}
				if ( context.megaMenu?.contains( window.document.activeElement ) ) {
					context.previousFocus?.focus();
				}
				context.previousFocus = null;
				context.megaMenu = null;
			}
		},
	},
} );
